import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ObjectActions from './product-speed-analysis.actions';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  ProductSpeedAnalysisDataInterface,
  ProductSpeedAnalysisDataTableColumnInterface,
  ProductSpeedAnalysisFilterInterface,
  ProductSpeedAnalysisInterface,
} from './product-speed-analysis.model';
import * as AppActions from '../../app/actions';
import { of } from 'rxjs';
import { GetManyResponseInterface } from '../../../shared/model/interface/crud-response-interface.model';
import { ProductSpeedAnalysisService } from '../../../shared/service/product-speed-analysis/product-speed-analysis.service';
import { Store } from '@ngrx/store';
import * as oeeAppReducer from '../../oee.reducer';
import { HttpParams } from '@angular/common/http';
import moment from 'moment';
import { minutesToHm, mysqlTimestampFormat } from '../../../shared/helper/date';
import _ from 'lodash';
import { AddProductSpeedResponseInterface } from '../../settings/product-speeds/product-speeds.model';
import { EntityTranslatorService } from '../../../shared/service/entity-translator/entity-translator.service';
import { HelperService } from '../../../shared/service/helper.service';
import { emptyAction } from '../../../../constants';

@Injectable()
export class ProductSpeedAnalysisEffects {
  constructor(
    private readonly actions$: Actions<ObjectActions.ProductSpeedAnalysisActions>,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly productSpeedAnalysisService: ProductSpeedAnalysisService,
    private readonly entityTranslation: EntityTranslatorService,
    private readonly helperService: HelperService,
  ) {}

  getProductSpeedAnalysisData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.PRODUCT_SPEED_ANALYSIS_DATA_LOADING),
      switchMap((objectData: ObjectActions.ProductSpeedAnalysisDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        const filterData: ProductSpeedAnalysisFilterInterface = objectData.productSpeedAnalysisFilter;
        let params: HttpParams = new HttpParams().append('siteIds', encodeURI(filterData.site.join(',')));

        if (Array.isArray(filterData.line)) {
          params = params.append('lineIds', encodeURI(filterData.line.join(',')));
        }

        if (Array.isArray(filterData.productIds)) {
          params = params.append('productIds', encodeURI(filterData.productIds.join(',')));
        }

        if (filterData.dateRange.startDate && filterData.dateRange.endDate) {
          params = params
            .append('countType', filterData.countType[0])
            .append('durationTypes', JSON.stringify(filterData.durationTypes))
            .append(
              'startDate',
              this.helperService.convertFromGivenTimezoneToUTCAsISOFormat(filterData.dateRange.startDate),
            )
            .append(
              'endDate',
              this.helperService.convertFromGivenTimezoneToUTCAsISOFormat(filterData.dateRange.endDate),
            );
        }

        return this.productSpeedAnalysisService.productSpeedAnalysisDataLoading(params).pipe(
          map((response: GetManyResponseInterface<ProductSpeedAnalysisInterface>) => {
            response.data.forEach((productSpeedAnalysis: ProductSpeedAnalysisInterface) => {
              this.entityTranslation.translate(productSpeedAnalysis);
            });
            response.data.forEach((e: ProductSpeedAnalysisInterface) => {
              ProductSpeedAnalysisService.setProductSpeedAnalysisProductName(e);
            });
            return this.convertData(response.data);
          }),
          switchMap((payload: ProductSpeedAnalysisInterface[]) => {
            return of(new ObjectActions.ProductSpeedAnalysisDataLoaded(payload), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  setProductSpeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.PRODUCT_SPEED_ANALYSIS_SET_PRODUCT_SPEED_LOAD),
      switchMap((objectData: ObjectActions.ProductSpeedAnalysisSetProductSpeedLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.productSpeedAnalysisService.setProductSpeed(objectData.productSpeed).pipe(
          switchMap((response: AddProductSpeedResponseInterface) => {
            return of(
              new ObjectActions.ProductSpeedAnalysisSetProductSpeedLoaded(response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => {
            return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  downloadExcel = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.PRODUCT_SPEED_ANALYSIS_DOWNLOAD_EXCEL),
      switchMap((objectData: ObjectActions.ProductSpeedAnalysisDownloadExcel) => {
        this.productSpeedAnalysisService.downloadExcel(objectData.tableData, objectData.countType, objectData.siteId);
        return emptyAction;
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  public convertData(productSpeedAnalysisData: ProductSpeedAnalysisInterface[]): ProductSpeedAnalysisInterface[] {
    productSpeedAnalysisData.forEach((data: ProductSpeedAnalysisDataTableColumnInterface) => {
      data.productUnit = _.camelCase(data.productUnit);
      data.catalogSpeed = ProductSpeedAnalysisEffects.getExpectedSpeed(data.originalData);
      data.originalData = data.originalData.map((originalDataRow: ProductSpeedAnalysisDataInterface) => {
        return {
          ...originalDataRow,
          isGoodData: Number(originalDataRow.selectedCount) > 0,
          quantityOrdered: originalDataRow.quantityOrdered ? originalDataRow.quantityOrdered.toString() : null,
          selectedCount: originalDataRow.selectedCount ? originalDataRow.selectedCount.toString() : null,
          summarySpeed: originalDataRow.summarySpeed ? originalDataRow.summarySpeed.toString() : null,
          productSpeed: originalDataRow.productSpeed ? originalDataRow.productSpeed.toString() : null,
          lineSpeed: originalDataRow.lineSpeed ? originalDataRow.lineSpeed.toString() : null,
          productLineSpeed: originalDataRow.productLineSpeed ? originalDataRow.productLineSpeed.toString() : null,
          formattedDuration: minutesToHm(originalDataRow.OEETotalDuration),
        };
      });
    });

    return productSpeedAnalysisData;
  }

  private static getExpectedSpeed(originalData: ProductSpeedAnalysisDataInterface[]): string {
    if (originalData.length > 0) {
      let expectedSpeed: string;
      const item = originalData[0];
      expectedSpeed = item.lineSpeed;

      if (item.productLineSpeed) {
        expectedSpeed = item.productLineSpeed.toString();
      } else if (item.productSpeed) {
        expectedSpeed = item.productSpeed.toString();
      }

      return expectedSpeed;
    }

    return null;
  }
}
